import { useState, useRef, useEffect, type ReactNode, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { dashboardNotifications, type DashboardNotification } from '@/mocks/dashboard';
import { listingsApi, leadsApi, auth, authApi } from '@/services/api';
import { useSettings } from '@/context/SettingsContext';

interface SidebarItem {
  label: string;
  href: string;
  icon: string;
  badge?: string;
  section?: string;
  adminOnly?: boolean;
}

const mainNavItems: SidebarItem[] = [
  { label: 'CRM Dashboard', href: '/dashboard', icon: 'ri-dashboard-line' },
  { label: 'Analytics', href: '/dashboard/analytics', icon: 'ri-bar-chart-line' },
  { label: 'Leads', href: '/dashboard/leads', icon: 'ri-user-search-line' },
  { label: 'Properties', href: '/dashboard/properties', icon: 'ri-building-line' },
  { label: 'Users', href: '/dashboard/users', icon: 'ri-group-line' },
  { label: 'Transactions', href: '/dashboard/transactions', icon: 'ri-exchange-funds-line' },
  { label: 'CRM Analytics', href: '/dashboard/crm-analytics', icon: 'ri-bar-chart-grouped-line' },
  { label: 'Reports', href: '/dashboard/reports', icon: 'ri-file-chart-line' },
  { label: 'Content', href: '/dashboard/content', icon: 'ri-file-edit-line' },
  { label: 'Media', href: '/dashboard/media', icon: 'ri-image-line' },
  { label: 'Menu Settings', href: '/dashboard/menu-settings', icon: 'ri-menu-2-line' },
  { label: 'Site Settings', href: '/dashboard/settings', icon: 'ri-settings-3-line' },
  { label: 'API Status', href: '/dashboard/api-status', icon: 'ri-wifi-line' },
  { label: 'Audit Log', href: '/dashboard/audit', icon: 'ri-history-line', adminOnly: true },
  { label: 'Security', href: '/dashboard/security', icon: 'ri-shield-check-line', adminOnly: true },
];

const quickActions: SidebarItem[] = [
  { label: 'Add Property', href: '/dashboard/properties', icon: 'ri-add-circle-line' },
  { label: 'Add Lead', href: '/dashboard/crm', icon: 'ri-user-add-line' },
  { label: 'Export Report', href: '/dashboard/reports', icon: 'ri-file-chart-line' },
];

const bottomItems: SidebarItem[] = [
  { label: 'Profile Settings', href: '/dashboard/profile', icon: 'ri-user-line' },
  { label: 'View Website', href: '/', icon: 'ri-global-line' },
];

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<DashboardNotification[]>(dashboardNotifications);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [propertiesCount, setPropertiesCount] = useState<number | null>(null);
  const [leadsCount, setLeadsCount] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const { settings } = useSettings();

  // Admin = super_admin/admin/owner → sees the Audit Log sidebar entry.
  const userRole = (auth.getRole() || '').toLowerCase();
  const isAdmin = ['super_admin', 'admin', 'owner'].includes(userRole);
  const visibleNavItems = mainNavItems.filter((item) => !item.adminOnly || isAdmin);

  // Fetch real counts for sidebar badges
  useEffect(() => {
    if (!auth.isAuthenticated()) return;
    listingsApi.stats()
      .then(r => {
        const data = r.data as Record<string, number> | null;
        if (data && data.total != null) {
          setPropertiesCount(data.total);
        }
      })
      .catch(() => { /* keep null — badge hidden on error */ });

    leadsApi.stats()
      .then(r => {
        const data = r.data as Record<string, number> | null;
        if (data && data.total != null) {
          setLeadsCount(data.total);
        }
      })
      .catch(() => { /* keep null */ });
  }, []);

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const getBreadcrumb = () => {
    const parts = location.pathname.split('/').filter(Boolean);
    return parts.map((p, i) => ({
      label: p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' '),
      href: '/' + parts.slice(0, i + 1).join('/'),
    }));
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleMarkAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
  }, []);

  const handleNotificationClick = useCallback((notif: DashboardNotification) => {
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, unread: false } : n));
    setNotifOpen(false);
    if (notif.linkTo) {
      navigate(notif.linkTo);
    }
  }, [navigate]);

  const handleLogout = async () => {
    // Best-effort server-side session revoke; always clear local auth.
    try {
      await authApi.logout();
    } catch {
      /* ignore — clear local session regardless */
    } finally {
      auth.logout();
      navigate('/admin');
    }
  };

  const getDynamicBadge = (item: SidebarItem): string | null => {
    if (item.href === '/dashboard/properties' && propertiesCount !== null) return String(propertiesCount);
    if (item.href === '/dashboard/leads' && leadsCount !== null) return String(leadsCount);
    return item.badge || null;
  };

  const renderNavItem = (item: SidebarItem, isQuickAction = false) => {
    const badge = getDynamicBadge(item);
    return (
      <Link
        key={item.href + item.label}
        to={item.href}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm transition-all cursor-pointer group ${
          isActive(item.href)
            ? 'bg-primary-500 text-white font-medium shadow-sm'
            : isQuickAction
              ? 'text-foreground-500 hover:text-foreground-950 hover:bg-background-100'
              : 'text-foreground-500 hover:text-foreground-950 hover:bg-background-100'
        }`}
      >
        <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isActive(item.href) ? 'text-white' : 'text-foreground-400 group-hover:text-foreground-700'}`}>
          <i className={`${item.icon} ${collapsed ? 'text-lg' : ''}`} />
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 whitespace-nowrap text-[13px]">{item.label}</span>
            {badge && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap leading-none ${isActive(item.href) ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'}`}>
                {badge}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background-100">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[998] bg-black/40 backdrop-blur-sm md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-[999] flex flex-col bg-foreground-950 border-r border-background-200 transition-all duration-300 h-full ${
          collapsed ? 'w-[68px]' : 'w-[260px]'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo / Brand */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-foreground-900 flex-shrink-0">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary-600 transition-colors"
          >
            {settings.general.logo_url ? (
              <img
                src={settings.general.logo_url}
                alt={settings.general.site_name || 'Logo'}
                className="max-h-6 max-w-[28px] object-contain"
              />
            ) : (
              <i className="ri-building-4-line text-white text-sm" />
            )}
          </button>
          {!collapsed && (
            <div className="flex items-center gap-1 min-w-0">
              {settings.general.show_site_name !== false ? (
                <>
                  <span className="font-heading font-bold text-sm text-white whitespace-nowrap">
                    CashMyProperty
                  </span>
                  <span className="font-heading font-bold text-sm text-primary-500">.ai</span>
                </>
              ) : (
                <span className="font-heading font-bold text-sm text-white whitespace-nowrap">
                  Dashboard
                </span>
              )}
            </div>
          )}
        </div>

        {/* User mini-profile */}
        {!collapsed && (
          <div className="px-4 py-3 border-b border-foreground-900 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-foreground-950">A</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white truncate">admin - RMS</p>
              <p className="text-[10px] text-foreground-500">admin</p>
            </div>
          </div>
        )}

        {/* Nav items */}
        <nav className="flex-1 py-4 px-2.5 space-y-1 overflow-y-auto">
          {visibleNavItems.map((item) => renderNavItem(item))}
        </nav>

        {/* Quick Actions */}
        {!collapsed && (
          <div className="px-4 py-2 border-t border-foreground-900">
            <p className="text-[9px] font-semibold text-foreground-500 uppercase tracking-widest mb-2">Quick Actions</p>
            <div className="space-y-1">
              {quickActions.map((item) => renderNavItem(item, true))}
            </div>
          </div>
        )}
        {collapsed && <div className="border-t border-foreground-900 mx-2.5" />}

        {/* Bottom items */}
        <div className="px-2.5 py-3 border-t border-foreground-900 space-y-1 flex-shrink-0">
          {bottomItems.map((item) => renderNavItem(item))}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-foreground-950 cursor-pointer flex-shrink-0"
            >
              <i className="ri-menu-3-line text-lg" />
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="hidden md:flex w-8 h-8 items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer flex-shrink-0"
            >
              <i className={`ri-${collapsed ? 'menu-unfold' : 'menu-fold'}-line text-lg`} />
            </button>
            <div className="text-sm text-foreground-400 hidden sm:flex items-center gap-1.5 truncate">
              {getBreadcrumb().map((crumb, i) => (
                <span key={crumb.href} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-foreground-300">/</span>}
                  <Link to={crumb.href} className="hover:text-foreground-700 transition-colors whitespace-nowrap">{crumb.label}</Link>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Global Search */}
            <div className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-700 cursor-pointer border border-background-200"
              >
                <i className="ri-search-line" />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-background-200 rounded-xl shadow-lg p-3 z-50">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                    <input
                      type="text"
                      autoFocus
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search properties, leads, users..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  {searchQuery && (
                    <div className="mt-2 space-y-1 max-h-60 overflow-y-auto text-xs">
                      <p className="text-[10px] text-foreground-400 uppercase tracking-wider px-2 py-1">Quick Results</p>
                      {['Commercial Tower - MG Road', 'Amit Patel (Lead)', 'Warehouse - Bhiwandi', '3BHK Powai'].filter(r => r.toLowerCase().includes(searchQuery.toLowerCase())).map(r => (
                        <div key={r} className="px-2 py-1.5 rounded-lg hover:bg-background-100 text-foreground-950 cursor-pointer">{r}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-400 hover:text-foreground-700 cursor-pointer border border-background-200"
              >
                <i className="ri-notification-3-line" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1">{unreadCount}</span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-background-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-background-200">
                    <span className="text-sm font-semibold text-foreground-950">Notifications</span>
                    {unreadCount > 0 && (
                      <button onClick={handleMarkAllRead} className="text-[10px] text-accent-700 hover:text-accent-500 cursor-pointer whitespace-nowrap">Mark all read</button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <i className="ri-notification-off-line text-2xl text-foreground-400 mb-2 block" />
                        <p className="text-xs text-foreground-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((n) => (
                        <div
                          key={n.id}
                          onClick={() => handleNotificationClick(n)}
                          className={`px-4 py-3 border-b border-background-100 hover:bg-background-50 cursor-pointer transition-colors ${n.unread ? 'bg-accent-50/40' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              n.type === 'lead' ? 'bg-accent-100 text-accent-700' :
                              n.type === 'property' ? 'bg-teal-500/15 text-teal-500' :
                              n.type === 'deal' ? 'bg-green-500/15 text-green-500' :
                              n.type === 'reminder' ? 'bg-rose-500/15 text-rose-400' :
                              'bg-secondary-100 text-secondary-700'
                            }`}>
                              <i className={`${n.icon} text-sm`} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {n.unread && <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0" />}
                                <p className="text-xs text-foreground-950 leading-relaxed">{n.text}</p>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-[10px] text-foreground-400">{n.time}</p>
                                <span className="text-[9px] text-foreground-400 uppercase bg-background-100 px-1.5 py-0.5 rounded">{n.type}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-2.5 border-t border-background-200 bg-background-50">
                    <Link to="/dashboard" className="text-[11px] text-accent-700 hover:text-accent-500 cursor-pointer font-medium">View all activity →</Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">A</span>
                </div>
                <i className={`ri-arrow-down-s-line text-foreground-400 text-sm transition-transform ${profileOpen ? 'rotate-180' : ''} hidden sm:block`} />
              </button>
              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-background-200 rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-background-200">
                    <p className="text-sm font-medium text-foreground-950">Admin User</p>
                    <p className="text-[11px] text-foreground-400">admin@cashmyproperty.ai</p>
                  </div>
                  <div className="py-1">
                    <Link to="/dashboard/profile" className="w-full text-left px-4 py-2 text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-950 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                      <i className="ri-user-settings-line text-sm" /> Edit Profile
                    </Link>
                    <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                      <i className="ri-logout-box-line text-sm" /> Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-background-50">
          <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}